import { g as getRoomType } from './get-room-type.js';
import { t as translateDate } from './translate-date.js';
import './index2.js';

/**
 * **Orbebooking**
 *
 * @exports bs_37
 * @category engines
 */
function factory({ window, document, thn }) {

  const engine = {};
  const dataLayer = window.dataLayer;

  engine.languageCheck = function languageCheck(ctx) {

    return new Promise(function (resolve, reject) {
      const isBookingEngine = window.location.hostname === 'reservations.orbebooking.com';

      const langEl = isBookingEngine ? document.querySelector('.lang')?.textContent : document.documentElement.lang;
      const lang = langEl?.slice(0, 2);

      if (lang && ctx.langs.test(lang)) {
        resolve(lang);
        return;
      }

      resolve('en');

    });
  };

  /**
     * @memberOf module:bs_37
     * @this thn.widgets.price
     * @see {@link Price#observe_dom}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceObserveDom = function priceObserveDom(ctx) {

    const container = document.querySelector('#accordion');

    if (container && !thn.orbebookingPriceObserver) {

      thn.orbebookingPriceObserver = function () {
        ctx.widget.logger.debug('*************** DOM Changed **************');
        ctx.widget.update();
      };

      thn.observeDom(container, thn.orbebookingPriceObserver);

      thn.pageUnload(function () {
        thn.stopObserveDom(container, thn.orbebookingPriceObserver);
        delete thn.orbebookingPriceObserver;
      });
    }

  };

  const hotelCodePidMap = {
    1156530: 'WYTK7', // Hotel Belmar
    1045842: '9afnB', // Nantipa
    1052967: 'FEgnf', // Hotel Grano de Oro
    1053920: 'iAswx', // Chachagua Rainforest Hotel
    1053921: 'tly6h', // Bahia del Sol Hotel
    1054011: 'aAldY', // The Royal Corin
    1054551: 'Yguuo', // Amor Arenal
    1054818: 'xrjXH', // Hotel Santa Teresa by the Beach
    1054937: 'surial', // Bosque del Mar
    1155780: '3gtBa', // Cuna del Angel
    1156166: 'S1ePo', // Gaia Hotel & Reserve
    1156479: 'plazare', // Tango Mar
  };

  /**
     * @memberOf module:bs_37
     * @see {@link generate_search_url}
     *
     * @param {object} ctx - The context object.
     */
  engine.generateSearchUrl = function generateSearchUrl(ctx) {

    const url = new URL(ctx.baseUrl);
    const validUrl = url.hostname.match(/reservations/i);
    const hotelCode = hotelCodePidMap[thn.data.propertyId]
    || document.querySelector('#orbeCode')?.value
    || url.pathname.split('/')[3];

    if (!validUrl && !hotelCode) {
      ctx.newUrl = '';
      return ctx;
    }

    ctx.newData.dateBegin = ctx.data.dateBegin.replace(/\//g, '');
    ctx.newData.dateEnd = ctx.data.dateEnd.replace(/\//g, '');

    // Export into ctx.newUrl
    url.pathname = `/Search/SearchAvailability/${hotelCode}/${thn.data.language}/${ctx.newData.dateBegin}/${ctx.newData.dateEnd}/${ctx.data.adults}/${ctx.data.children}`;
    if (ctx.data.children) {
      url.pathname += `/false/false/false/false/${ctx.data.roomsDispo?.[0].childrenAges[0]}`;
      for (let i = 1; i < ctx.data.roomsDispo?.[0].childrenAges.length; i += 1) {
        url.pathname += `,${ctx.data.roomsDispo?.[0].childrenAges[i]}`;
      }
    }

    ctx.newUrl = url.toString();

    return ctx;
  };

  /**
    *
    * @memberOf module:bs_37
    *
    */
  const roomDetailsParseData = (adults, children) => {
    const roomsArray = [...document.querySelectorAll('.room-block .col-right')].filter((roomEl) => !roomEl.querySelector('.roomNoAvailData'));

    if (thn.empty(roomsArray)) {
      return {};
    }

    const roomsInfo = roomsArray
      .map((room) => {
        const roomName = room.querySelector('.textbox h1')?.textContent;
        const roomType = getRoomType(roomName);
        const roomRates = [...room.querySelectorAll('.room_row_rate')]
          .map((rate) => {
            const rateName = rate.querySelector('.subtitle')?.textContent;
            const price = thn.parseNumber(rate.querySelector('.price, .new-price')?.textContent);
            const hasBreakfast = !!rateName.match(/desayuno|breakfast/i);
            const isRefundable = !!rate.querySelector('.privacy > span')?.textContent.match('Cancelación Gratuita|Free cancellation');
            const infoRate = {
              id: '',
              name: rateName,
              totalNight: price,
              breakfast: hasBreakfast,
              refundable: isRefundable,
            };

            return infoRate;
          });

        return {
          id: '',
          name: roomName,
          adults: adults,
          children: children,
          type: roomType,
          prices: roomRates,
        };

      }).filter(Boolean);

    return roomsInfo;

  };

  /**
     * @memberOf module:bs_37
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData = function priceParseData(ctx) {

    const widgetData = {};
    let nights;
    const dateBegin = translateDate(document.querySelector('#dateinicio_dp')?.value, thn.data.language);
    const dateEnd = translateDate(document.querySelector('#datefin_dp')?.value, thn.data.language);

    if (dateBegin && dateEnd) {
      widgetData.dateBegin = dateBegin;
      widgetData.dateEnd = dateEnd;
      nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
    }
    // Guests, Adults & Children
    widgetData.roomsDispo = [];

    const currentRoom = thn.parseNumber(document.querySelector('#room-selector-number')?.textContent) || 1;
    const adults = thn.parseNumber(document.querySelector(`#adultos_${currentRoom}`)?.value) || 2;
    const children = thn.parseNumber(document.querySelector(`#ninos_${currentRoom}`)?.value) || 0;
    const roomInfo = {
      adults,
      children,
      childrenAges: [],
    };

    if (children) {
      for (let i = 1; i <= children; i += 1) {
        const childreAge = thn.parseNumber(document.querySelector(`#child_ages_room_${currentRoom}_child_${i}`)?.value);
        roomInfo.childrenAges.push(childreAge);
      }
    }

    widgetData.roomsDispo = [roomInfo];

    const usersToExcludeNationalPrices = [1046458, 1046458].includes(thn.data.accountUserId);
    const filteredRooms = [...document.querySelectorAll('div[id*=room_content_] .offer-box.room-rate-display-box')].filter((rate) => !rate.querySelector('div.text-col > .subtitle')?.textContent?.match(/tarifa nacionales/i));
    const domRooms = usersToExcludeNationalPrices ? [...filteredRooms].map((room) => room.querySelector('.price, .new-price')).filter(Boolean) : [...document.querySelectorAll('.price, .new-price')];
    const domPrices = domRooms.map((room) => thn.parseNumber(room?.textContent)).filter(Boolean);
    const dataLayerRooms = thn.objectGet(dataLayer, 'impressions', { first: false });
    const dataLayerPrices = dataLayerRooms.map((item) => item.price / nights);
    const prices = domPrices || dataLayerPrices;
    const currency = thn.objectGet(dataLayer, 'currencyCode', { first: false });

    if (thn.notEmpty(prices)) {
      widgetData.price = Math.min(...prices);
      widgetData.currency = currency;
      widgetData.roomDetails = roomDetailsParseData(adults, children);
    } else {
      widgetData.noRooms = true;
    }
    return widgetData;
  };

  /**
     * TODO: implement **bookingId**.
     *
     * @memberOf module:bs_37
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(ctx) {

    const widgetData = {};

    const purchaseInputValue = JSON.parse(document.querySelector('[id*=geh_purchaseObj]')?.value)?.actionField;
    const bookingId = thn.objectGet(dataLayer, 'id') || thn.objectGet(dataLayer, 'transaction_id') || purchaseInputValue?.id;

    if (!bookingId) {
      thn.logger.debug('Booking Id Missing...');
      widgetData.set = false;
      return widgetData;
    }

    widgetData.bookingId = bookingId;

    const datesEl = document.querySelectorAll('.dates p');
    const dates = [...datesEl].map((date) => date?.innerText.split(':')[1]?.replace(/,/, ''));
    const dateBegin = translateDate(dates[0], thn.data.language);
    const dateEnd = translateDate(dates[1], thn.data.language);

    widgetData.dateBegin = dateBegin;
    widgetData.dateEnd = dateEnd;

    let price;
    let currency;
    const purchase = thn.objectGet(dataLayer, 'purchase')?.actionField;
    if (purchase) {
      const priceWithoutTax = thn.parseNumber(purchase?.revenue);
      const tax = thn.parseNumber(purchase?.tax);
      price = typeof priceWithoutTax && typeof tax === 'number' && priceWithoutTax + tax;
      currency = thn.objectGet(dataLayer, 'currency');
    } else {
      price = thn.parseNumber(document.querySelector('.holder.foot strong')?.textContent) || thn.parseNumber(purchaseInputValue?.revenue);
      currency = thn.parseCurrency(document.querySelector('.holder.foot strong')?.textContent) || 'USD';
    }

    widgetData.price = price;
    widgetData.currency = currency;

    return widgetData;
  };

  return engine;
}

export { factory };
